import { AsyncPipe, CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
} from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { filter, Observable } from 'rxjs';

import { UserProfileDialogComponent } from '@blaze-dashboard/user-profile-feature';
import { UserInfo } from '@blaze-shared/api-contracts-domain';
import { fromRouter, ProjectContextFacade } from '@blaze-shared/app-state-data-access';
import { AuthService } from '@blaze-shared/auth-data-access';
import { AvatarComponent } from '@blaze-shared/shared-feature';

@UntilDestroy()
@Component({
  imports: [AsyncPipe, AvatarComponent, CommonModule, MatIconModule, MatMenuModule],
  selector: 'blaze-action-bar-user',
  templateUrl: './action-bar-user.component.html',
  styleUrls: ['./action-bar-user.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActionBarUserComponent {
  private readonly authService = inject(AuthService);
  private readonly dialog = inject(MatDialog);
  private readonly projectContextFacade = inject(ProjectContextFacade);
  private readonly router = inject(Router);
  private readonly store = inject(Store);

  private readonly routerProjectId = toSignal(this.store.select(fromRouter.getProjectId), {
    initialValue: null,
  });
  private readonly pendingNav = signal(false);
  protected readonly isProjectUrl = computed(() => this.pendingNav() || !!this.routerProjectId());
  protected readonly user$: Observable<UserInfo | undefined>;

  constructor() {
    this.user$ = this.projectContextFacade.userInfo$.pipe(
      filter((userInfo): userInfo is UserInfo => !!userInfo),
    );

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationStart),
        untilDestroyed(this),
      )
      .subscribe(() => {
        this.pendingNav.set(true);
      });

    this.router.events
      .pipe(
        filter(
          (event) =>
            event instanceof NavigationEnd ||
            event instanceof NavigationCancel ||
            event instanceof NavigationError,
        ),
        untilDestroyed(this),
      )
      .subscribe(() => {
        this.pendingNav.set(false);
      });
  }

  protected onOpenUserProfile(): void {
    this.dialog.open(UserProfileDialogComponent, {
      data: null,
      disableClose: false,
      hasBackdrop: true,
      maxWidth: '552px',
      panelClass: ['bd', 'bd-dialog'],
      width: '552px',
    });
  }

  protected onSignOut(): void {
    this.authService.signOut();
  }
}
